import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { useEffect, useState } from 'react'
import { ToastProvider } from './context/ToastProvider'
import { ToastContainer } from './components/ToastContainer'
import { LoginPage } from './pages/LoginPage'
import { LogoutPage } from './pages/LogoutPage'
import { AuthErrorPage } from './pages/AuthErrorPage'
import { LoadingSpinner } from './components/LoadingSpinner'
import { triggerAccountBlocked } from './services/api-client'
import { bootstrapMe, getMe } from './services/auth-api'
import { consumeIntendedLocation } from './utils/session'
import { FEATUREBASE_SSO_PATH } from '../lib/featurebase-sso'
import type { MeResponse } from './types'

type PostLoginResolution =
  | { type: 'path'; path: string }
  | { type: 'unauthorized' }
  | { type: 'temporary_error' }
  | { type: 'no_tenant' }

/**
 * Maps a /api/users/me response to a post-login redirect path.
 */
function resolvePathFromMe(data: MeResponse): PostLoginResolution {
  if (data.tenant?.slug) {
    // Check if the user was trying to access a specific page before login
    const intendedLocation = consumeIntendedLocation()
    // Also resume the Featurebase SSO page, which isn't under a tenant slug.
    if (
      intendedLocation &&
      (intendedLocation.startsWith(`/${data.tenant.slug}/`) ||
        intendedLocation.startsWith(FEATUREBASE_SSO_PATH))
    ) {
      return { type: 'path', path: intendedLocation }
    }

    const perms: string[] = data.permissions ?? []
    const hasDashboard = perms.includes('dashboard.view')
    const hasSearch = perms.includes('products.search')

    if (hasDashboard) return { type: 'path', path: `/${data.tenant.slug}/dashboard` }
    if (hasSearch) return { type: 'path', path: `/${data.tenant.slug}/engineer` }
    return { type: 'path', path: `/${data.tenant.slug}/no-access` }
  }
  return { type: 'no_tenant' }
}

/**
 * Classifies an error from /api/users/me into a PostLoginResolution.
 * Throws for account-blocked so the caller doesn't navigate.
 */
function classifyMeError(err: unknown): PostLoginResolution {
  if (err instanceof Error && err.message === 'Account blocked') throw err
  if (typeof err === 'object' && err !== null && 'status' in err) {
    const typedErr = err as { status: number; data?: Record<string, unknown> }
    if (typedErr.status === 401 || typedErr.status === 0) {
      return { type: 'unauthorized' }
    }
    if (typedErr.status === 403) {
      const data = typedErr.data || {}
      const detail = typeof data.detail === 'string' ? data.detail.toLowerCase() : ''
      if (
        data.code === 'account_blocked' ||
        detail.includes('deactivated') ||
        detail.includes('has been deleted') ||
        detail.includes('no longer active') ||
        detail.includes('organisation') ||
        detail.includes('organization')
      ) {
        triggerAccountBlocked({ status: 403, data })
        throw new Error('Account blocked')
      }
    }
  }
  return { type: 'temporary_error' }
}

/**
 * Fetches /api/users/me to resolve the tenant slug for post-login redirect.
 *
 * Tries bootstrapMe (AuthKit token) first. If that fails — e.g. AuthKit
 * hasn't finished processing the new session yet — falls back to getMe()
 * which uses apiClient's resolveAccessToken() with built-in retries.
 */
async function resolvePostLoginPath(
  getAccessToken: () => Promise<string>,
): Promise<PostLoginResolution> {
  try {
    return resolvePathFromMe(await bootstrapMe(getAccessToken))
  } catch (bootstrapErr) {
    // Account blocked — surface immediately, don't retry
    if (bootstrapErr instanceof Error && bootstrapErr.message === 'Account blocked') throw bootstrapErr

    // bootstrapMe failed (AuthKit not ready, code already consumed, etc.)
    // Fall back to getMe() with retries for transient 403s during callback.
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        return resolvePathFromMe(await getMe())
      } catch (fallbackErr) {
        if (fallbackErr instanceof Error && fallbackErr.message === 'Account blocked') throw fallbackErr
        const status = typeof fallbackErr === 'object' && fallbackErr !== null && 'status' in fallbackErr
          ? (fallbackErr as { status: number }).status
          : 0
        // Retry on 403 (permission not ready yet) — but not on last attempt
        if (status === 403 && attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, 800))
          continue
        }
        // eslint-disable-next-line no-console
        console.error('Failed to resolve post-login path:', fallbackErr)
        return classifyMeError(fallbackErr)
      }
    }
    return { type: 'temporary_error' }
  }
}

/**
 * Handles the OAuth callback — exchanges the code with the backend,
 * stores tokens in localStorage, then resolves tenant and redirects.
 *
 * Also handles invitation links: when an invited user clicks the email link,
 * WorkOS redirects here with a code, but AuthKit can't exchange it (no PKCE
 * verifier since signIn() was never called). We detect this (!user after
 * loading) and auto-call signIn() to start a proper PKCE flow. WorkOS will
 * auto-authenticate since the user already has a session from the invitation.
 */
function CallbackPage() {
  const { user, isLoading, signIn, getAccessToken } = useAuth()
  const navigate = useNavigate()
  const [resolving, setResolving] = useState(false)

  // Capture code immediately during render, before AuthKit cleans the URL
  const [capturedCode] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('code')
  })

  useEffect(() => {
    if (isLoading || resolving) return

    if (user) {
      sessionStorage.removeItem('auth_callback_retry')
      setResolving(true)

      const code = capturedCode

      // If we have a code, exchange it with our backend to get tokens
      // NOTE: This might fail if AuthKit has already used the code, since OAuth codes
      // are single-use. However, testing shows WorkOS allows the same code to be
      // exchanged multiple times within a short window, making this approach work.
      if (code && !localStorage.getItem('refresh_token')) {
        fetch(`${import.meta.env.VITE_API_URL}/api/auth/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        })
          .then(res => {
            if (!res.ok) {
              // Code exchange failed - this is expected if AuthKit used it first
              // Fall back to using AuthKit's session without localStorage persistence
              console.warn('Backend token exchange failed, using AuthKit session only')
              return null
            }
            return res.json()
          })
          .then(data => {
            // Store tokens in localStorage for persistence across refreshes
            if (data?.access_token && data?.refresh_token) {
              localStorage.setItem('access_token', data.access_token)
              localStorage.setItem('refresh_token', data.refresh_token)
              // Tokens stored — now resolve path
              return resolvePostLoginPath(getAccessToken)
            }
            // Code exchange failed — tokens not stored.
            // Return null so the .then chain skips to the no-code path below
            return null
          })
          .then((result) => {
            if (!result) return // Backend exchange failed, will retry below
            if (result.type === 'path') {
              navigate(result.path, { replace: true })
              return
            }
            if (result.type === 'no_tenant') {
              sessionStorage.setItem('logout_reason', 'no_tenant')
              navigate('/auth/error', { replace: true })
              return
            }
            if (result.type === 'unauthorized') {
              sessionStorage.setItem('logout_reason', 'session_expired')
              navigate('/auth/error', { replace: true })
              return
            }
            sessionStorage.setItem('logout_reason', 'backend_unavailable')
            navigate('/auth/error', { replace: true })
          })
          .catch((err) => {
            if (err instanceof Error && err.message === 'Account blocked') {
              sessionStorage.setItem('logout_reason', 'account_blocked')
              sessionStorage.setItem('logout_message', 'Your account is no longer active. Please contact support.')
              localStorage.removeItem('access_token')
              localStorage.removeItem('refresh_token')
              navigate('/auth/error', { replace: true })
              return
            }
            console.warn('Token storage failed, using AuthKit session only')
          })
        return
      }

      // No code in URL or tokens already stored, just resolve path
      resolvePostLoginPath(getAccessToken)
        .then((result) => {
          if (result.type === 'path') {
            navigate(result.path, { replace: true })
            return
          }
          if (result.type === 'no_tenant') {
            sessionStorage.setItem('logout_reason', 'no_tenant')
            navigate('/auth/error', { replace: true })
            return
          }
          if (result.type === 'unauthorized') {
            sessionStorage.setItem('logout_reason', 'session_expired')
            navigate('/auth/error', { replace: true })
            return
          }
          sessionStorage.setItem('logout_reason', 'backend_unavailable')
          navigate('/auth/error', { replace: true })
        })
        .catch((err) => {
          if (err instanceof Error && err.message === 'Account blocked') {
            sessionStorage.setItem('logout_reason', 'account_blocked')
            sessionStorage.setItem('logout_message', 'Your account is no longer active. Please contact support.')
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            navigate('/auth/error', { replace: true })
          }
        })
      return
    }

    // Code exchange failed (e.g. invitation link — no PKCE verifier).
    // Retry once via signIn() to establish a proper PKCE flow.
    if (!sessionStorage.getItem('auth_callback_retry')) {
      sessionStorage.setItem('auth_callback_retry', '1')
      signIn()
    } else {
      // Already retried — avoid infinite loop, send to login
      sessionStorage.removeItem('auth_callback_retry')
      navigate('/auth/login', { replace: true })
    }
  }, [user, isLoading, navigate, signIn, getAccessToken, resolving])

  return <LoadingSpinner message="Completing sign in..." />
}

export function AuthApp() {
  return (
    <>
      <ToastProvider>
        <ToastContainer />
        <Routes>
          {/* Auth routes (relative to /auth/*) */}
          <Route path="login" element={<LoginPage />} />
          <Route path="logout" element={<LogoutPage />} />
          <Route path="callback" element={<CallbackPage />} />
          <Route path="error" element={<AuthErrorPage />} />

          {/* Catch-all: redirect stale /onboarding/* URLs */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ToastProvider>
    </>
  )
}
